import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Paper, Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Chip, IconButton, TextField, InputAdornment,
  Menu, MenuItem, CircularProgress, Alert, Dialog, DialogTitle,
  DialogContent, DialogActions, Button, Grid, Tooltip,
} from '@mui/material';
import {
  Search as SearchIcon, MoreVert as MoreVertIcon, Edit as EditIcon,
  Add as AddIcon, QrCode as QrCodeIcon, Visibility as ViewIcon,
  Store as StoreIcon,
} from '@mui/icons-material';
import wagService, { WAGAgencia } from '../../services/wag.service';

const WAGAgencias: React.FC = () => {
  const [items, setItems] = useState<WAGAgencia[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selected, setSelected] = useState<WAGAgencia | null>(null);

  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState<Partial<WAGAgencia>>({});
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const [editOpen, setEditOpen] = useState(false);
  const [qrOpen, setQrOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setItems(await wagService.getAgencias());
    } catch {
      setError('Erro ao carregar agências');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = items.filter(i =>
    !search || i.desig.toLowerCase().includes(search.toLowerCase()) || i.codigo.toLowerCase().includes(search.toLowerCase())
  );

  async function handleCreate() {
    setSaving(true);
    setSaveError(null);
    try {
      await wagService.createAgencia(form);
      setCreateOpen(false);
      setForm({});
      load();
    } catch (e: any) {
      setSaveError(e?.response?.data?.error || 'Erro ao criar agência');
    } finally {
      setSaving(false);
    }
  }

  async function handleEdit() {
    if (!selected) return;
    setSaving(true);
    setSaveError(null);
    try {
      await wagService.updateAgencia(selected.id, form);
      setEditOpen(false);
      load();
    } catch (e: any) {
      setSaveError(e?.response?.data?.error || 'Erro ao actualizar');
    } finally {
      setSaving(false);
    }
  }

  async function toggleEstado() {
    if (!selected) return;
    await wagService.updateAgencia(selected.id, { estado: selected.estado === 'A' ? 'I' : 'A' });
    setAnchorEl(null);
    load();
  }

  function openCreate() { setForm({}); setSaveError(null); setCreateOpen(true); }
  function openEdit(ag: WAGAgencia) { setForm({ desig: ag.desig, descr: ag.descr, mensagem: ag.mensagem, endereco: ag.endereco }); setSaveError(null); setEditOpen(true); setAnchorEl(null); }
  function openQr(ag: WAGAgencia) { setSelected(ag); setQrOpen(true); setAnchorEl(null); }

  const kioskUrl = (ag: WAGAgencia) => `${window.location.protocol}//wag.cv/kiosk/${ag.qrToken}`;
  const displayUrl = (ag: WAGAgencia) => `${window.location.protocol}//wag.cv/display/${ag.id}`;

  return (
    <Box p={3}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h5" fontWeight={700}>Agências WAG</Typography>
          <Typography variant="body2" color="text.secondary">Locais/pontos de atendimento</Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}
          sx={{ bgcolor: '#e94560', '&:hover': { bgcolor: '#c73350' } }}>
          Nova Agência
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Paper sx={{ p: 2, mb: 3, borderRadius: 2 }}>
        <TextField fullWidth placeholder="Pesquisar por nome ou código…" value={search}
          onChange={e => setSearch(e.target.value)} size="small"
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }} />
      </Paper>

      <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: 'action.hover' }}>
              <TableCell>Código</TableCell>
              <TableCell>Designação</TableCell>
              <TableCell>Serviços</TableCell>
              <TableCell>Balcões</TableCell>
              <TableCell>Estado</TableCell>
              <TableCell align="right">Acções</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={6} align="center" sx={{ py: 4 }}><CircularProgress size={24} /></TableCell></TableRow>
            ) : filtered.length === 0 ? (
              <TableRow><TableCell colSpan={6} align="center" sx={{ py: 4, color: 'text.secondary' }}>Nenhuma agência encontrada</TableCell></TableRow>
            ) : filtered.map(ag => (
              <TableRow key={ag.id} hover>
                <TableCell><Chip label={ag.codigo} size="small" variant="outlined" /></TableCell>
                <TableCell>
                  <Typography fontWeight={600}>{ag.desig}</Typography>
                  {ag.endereco && <Typography variant="caption" color="text.secondary">{ag.endereco}</Typography>}
                </TableCell>
                <TableCell>{ag.totalServicos}</TableCell>
                <TableCell>{ag.totalBalcoes}</TableCell>
                <TableCell>
                  <Chip label={ag.estado === 'A' ? 'Activa' : 'Inactiva'} size="small"
                    color={ag.estado === 'A' ? 'success' : 'default'} />
                </TableCell>
                <TableCell align="right">
                  <Tooltip title="Ver QR code e links">
                    <IconButton size="small" onClick={() => { setSelected(ag); openQr(ag); }}>
                      <QrCodeIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <IconButton size="small" onClick={e => { setSelected(ag); setAnchorEl(e.currentTarget); }}>
                    <MoreVertIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Context menu */}
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
        <MenuItem onClick={() => selected && openEdit(selected)}><EditIcon fontSize="small" sx={{ mr: 1 }} />Editar</MenuItem>
        <MenuItem onClick={() => selected && openQr(selected)}><QrCodeIcon fontSize="small" sx={{ mr: 1 }} />QR Code / Links</MenuItem>
        <MenuItem onClick={toggleEstado} sx={{ color: selected?.estado === 'A' ? 'error.main' : 'success.main' }}>
          {selected?.estado === 'A' ? 'Desactivar' : 'Activar'}
        </MenuItem>
      </Menu>

      {/* Dialog Criar */}
      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Nova Agência</DialogTitle>
        <DialogContent>
          {saveError && <Alert severity="error" sx={{ mb: 2 }}>{saveError}</Alert>}
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={4}>
              <TextField label="Código *" fullWidth size="small" value={form.codigo ?? ''}
                onChange={e => setForm(p => ({ ...p, codigo: e.target.value.toUpperCase() }))}
                helperText="Ex: AGCID01" />
            </Grid>
            <Grid item xs={8}>
              <TextField label="Designação *" fullWidth size="small" value={form.desig ?? ''}
                onChange={e => setForm(p => ({ ...p, desig: e.target.value }))} />
            </Grid>
            <Grid item xs={12}>
              <TextField label="Endereço" fullWidth size="small" value={form.endereco ?? ''}
                onChange={e => setForm(p => ({ ...p, endereco: e.target.value }))} />
            </Grid>
            <Grid item xs={12}>
              <TextField label="Mensagem no quiosque" fullWidth size="small" multiline rows={2}
                value={form.mensagem ?? ''}
                onChange={e => setForm(p => ({ ...p, mensagem: e.target.value }))}
                helperText="Texto exibido quando o cliente retira a senha" />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleCreate} disabled={saving || !form.codigo || !form.desig}
            sx={{ bgcolor: '#e94560', '&:hover': { bgcolor: '#c73350' } }}>
            {saving ? 'A criar…' : 'Criar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog Editar */}
      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Editar — {selected?.desig}</DialogTitle>
        <DialogContent>
          {saveError && <Alert severity="error" sx={{ mb: 2 }}>{saveError}</Alert>}
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <TextField label="Designação" fullWidth size="small" value={form.desig ?? ''}
                onChange={e => setForm(p => ({ ...p, desig: e.target.value }))} />
            </Grid>
            <Grid item xs={12}>
              <TextField label="Endereço" fullWidth size="small" value={form.endereco ?? ''}
                onChange={e => setForm(p => ({ ...p, endereco: e.target.value }))} />
            </Grid>
            <Grid item xs={12}>
              <TextField label="Mensagem no quiosque" fullWidth size="small" multiline rows={2}
                value={form.mensagem ?? ''}
                onChange={e => setForm(p => ({ ...p, mensagem: e.target.value }))} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleEdit} disabled={saving}
            sx={{ bgcolor: '#e94560', '&:hover': { bgcolor: '#c73350' } }}>
            {saving ? 'A guardar…' : 'Guardar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog QR Code / Links */}
      <Dialog open={qrOpen} onClose={() => setQrOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <QrCodeIcon /> {selected?.desig}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                  Link do Quiosque Virtual (QR Code)
                </Typography>
                <Typography variant="body2" fontFamily="monospace" sx={{ wordBreak: 'break-all' }}>
                  {selected && kioskUrl(selected)}
                </Typography>
                <Button size="small" startIcon={<QrCodeIcon />} sx={{ mt: 1 }}
                  onClick={() => selected && window.open(kioskUrl(selected), '_blank')}>
                  Abrir quiosque
                </Button>
              </Paper>
            </Grid>
            <Grid item xs={12}>
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                  Link do Painel TV / Display
                </Typography>
                <Typography variant="body2" fontFamily="monospace" sx={{ wordBreak: 'break-all' }}>
                  {selected && displayUrl(selected)}
                </Typography>
                <Button size="small" startIcon={<ViewIcon />} sx={{ mt: 1 }}
                  onClick={() => selected && window.open(displayUrl(selected), '_blank')}>
                  Abrir display
                </Button>
              </Paper>
            </Grid>
            <Grid item xs={12}>
              <Alert severity="info" icon={<StoreIcon />}>
                Imprima o QR code do link de quiosque e coloque na entrada. Os clientes lêem e retiram a senha sem necessidade de quiosque físico.
              </Alert>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setQrOpen(false)}>Fechar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default WAGAgencias;
